/**
 * 线下门店物料管理 —— 前端共享常量（唯一出口）
 *
 * 需求：物料管理：线下门店物料管理
 *      https://leoao-inc.feishu.cn/wiki/Jas6w5HRziyoEkkLHv9clukOnTb
 * 归属：物料管理页 + 二维码物料生成弹窗（本次开发）
 *
 * ⚠️ 协作约定（另一位同学的「物料生成记录页」「二维码绑定详情页」请只读引用本文件）
 *   1. 路由地址、跳转参数名、枚举、文案、文件名规则一律从本文件取，不要在页面内散写字符串；
 *   2. 需要新增枚举（如批次状态、是否绑定）请提 MR 合入本文件，禁止建平行常量文件；
 *   3. 枚举值 / 字段名已按《线下门店物料管理S端接口定义（前端对接版）v1.2》§2.4 定稿，
 *      本文件是接口口径在前端的唯一落地点；后端改字段只改本文件 + src/services/offlineMaterial.js 适配层。
 */

/** 页面标题（原型：放在筛选项下方、表格左上方，即 ProTable 的 headerTitle） */
export const PAGE_TITLE = '线下门店物料管理';

/**
 * 页面按钮文案（原型 2026-09-04 定稿）
 * ⚠️ 原「二维码生成记录」已按原型改名为「物料生成记录」，
 *    同事的记录页页内标题/面包屑请引用本常量，不要再写旧名。
 */
export const BTN = {
  RECORDS: '物料生成记录',
  /** 下一级页面（记录页 / 绑定详情页）页头「← 返回」按钮文案，两页共用单一来源 */
  BACK: '返回',
  GENERATE: '生成二维码物料',
  /** 生成记录页：行操作区（三键均已接线：下载 §3.4 / 绑定详情 §3.3 / 补全剩余 §3.6） */
  DOWNLOAD: '下载',
  BIND_DETAIL: '绑定详情',
  /**
   * 按契约 v1.2 §4 按钮矩阵用词改为「补全剩余」（旧写法「补足剩余」）。
   * 若产品原型确为“补足”，改回只改这一处。
   */
  TOPUP: '补全剩余',
  /**
   * ★§3.2 轮询接线（2026-09-11）：记录页「生成中」常驻提示条上的三个按钮。
   * 停止 / 继续 是同一个开关的两态，刷新 / 重试 是超时与失败两种停法的补救入口。
   */
  POLL_STOP: '停止自动刷新',
  POLL_RESUME: '继续自动刷新',
  POLL_RETRY_NOW: '刷新',
};

/** 路由地址（Umi 约定式路由：src/pages 目录即地址） */
export const ROUTE = {
  /** 物料管理页（本次） */
  MATERIAL_LIST: '/operating/offlineMaterial',
  /** 物料生成记录页（同事） */
  RECORDS: '/operating/offlineMaterial/records',
  /** 二维码绑定详情页（同事） */
  RECORDS_DETAIL: '/operating/offlineMaterial/records/detail',
};

/** 跨页面跳转参数名：只用 query，不用路由 params（刷新不丢、可复制分享） */
export const QUERY_KEY = {
  BATCH_NO: 'batchNo',
};

/** 物料类型（取值以后端定稿为准） */
export const MATERIAL_TYPE = {
  QR_CODE: 'QR_CODE',
  NFC: 'NFC',
};

/** ProTable valueEnum：物料类型列 + 筛选项共用 */
export const MATERIAL_TYPE_ENUM = {
  [MATERIAL_TYPE.QR_CODE]: { text: '二维码物料' },
  [MATERIAL_TYPE.NFC]: { text: 'NFC物料' },
};

/** 生成批次相关限制（上限对齐契约 §3.1：服务端可配，当前 1~100） */
export const BATCH_LIMIT = {
  MIN_QUANTITY: 1,
  MAX_QUANTITY: 100,
  TITLE_MAX: 50,
  REMARK_MAX: 200,
};

/**
 * 记录页轮询节奏（契约 §3.2 v1.2 硬要求，F14；2026-09-11 在 records/index.jsx 接线）。
 *
 * 服务端 v3.9 起「生成中」最坏要等约 30 分钟的强制收敛窗口（§8-A13），所以必须递减速 + 有上限：
 * 前 60s 每 5s → 之后降到 25s → 累计 5 分钟停止并挂 POLL_TIMEOUT_TIP 常驻提示。
 *
 * ⚠️ 快档由契约原稿的 3s 调成 5s（唯一一处偏离 §3.2 数值的改动，已同步回文档）：
 *   1. 需求侧的等待预期就是「几秒钟看一眼」，3s 与 5s 在体验上不可分辨；
 *   2. 出图是服务端异步批量任务，前 60s 内完成与否跟这 2 秒无关，省下的 40% 请求量是实打实的；
 *   3. 单次成本没变（仍是 page 列表查询），变的只是次数 —— 满打满算一轮 5 分钟 ≈ 12 + 10 = 22 个请求。
 * 改节奏只改这里，页面里不许出现第二个数字。
 */
export const POLL = {
  FAST_INTERVAL_MS: 5000,
  FAST_WINDOW_MS: 60000,
  SLOW_INTERVAL_MS: 25000,
  MAX_MS: 300000,
};

/**
 * 批次状态（定稿：0~3 整数，与 MaterialBatchController/page.md 共享枚举一致）
 * 0 生成中（创建/补足提交后进入，异步出图中）/ 1 生成成功 / 2 生成失败 / 3 部分成功
 */
export const BATCH_STATUS = {
  /** 生成中（创建/补足提交后进入） */
  GENERATING: 0,
  /** 生成成功 */
  SUCCESS: 1,
  /** 生成失败 */
  FAIL: 2,
  /** 部分成功（成功数 < 总数，可下载缺角包、可补足） */
  PARTIAL: 3,
};

/** ProTable valueEnum：生成状态（status 预设色，供 Tag/Badge/筛选共用） */
export const BATCH_STATUS_ENUM = {
  [BATCH_STATUS.GENERATING]: { text: '生成中', status: 'Processing' },
  [BATCH_STATUS.SUCCESS]: { text: '生成成功', status: 'Success' },
  [BATCH_STATUS.FAIL]: { text: '生成失败', status: 'Error' },
  [BATCH_STATUS.PARTIAL]: { text: '部分成功', status: 'Warning' },
};

/**
 * 生成状态展示文案（**记录页「生成详情」列 + 详情页「基础信息-生成状态」共用单一来源**）。
 *
 * 需求：状态后要带二维码生成情况（成功数/总数），如「生成成功（10/10）」。
 * 口径（两页必须一致，改需求只改这一处）：
 *   1. 文案优先用服务端 statusText（后端改文案不必发版），缺失回退 BATCH_STATUS_ENUM；
 *   2. **只在 1 生成成功 / 3 部分成功 且 totalCount > 0 时拼括号**：
 *      生成中拼会出现「生成中（3/100）」被当成卡住的进度、全失败拼会出现「生成失败（0/100）」，
 *      两者都不是需求要的「生成情况」，且需求原文只给了成功态示例；
 *   3. 部分成功一定带括号（96/100），是运营判断「还差几张、要不要补全」的依据。
 *
 * @param {{status?:number,statusText?:string,successCount?:number,totalCount?:number}} [batch] 批次概要/列表行
 * @returns {string} 如「生成成功（10/10）」「部分成功（96/100）」「生成中」
 */
export const BATCH_STATUS_LABEL = (batch) => {
  // 自己兑 null：详情页数据未回来时 batch 就是 null，而默认参数 `= {}` 只挡 undefined，
  // 没拦住会在解构处抛错、整页白屏（已实测）。
  const { status, statusText, successCount, totalCount } = batch || {};
  const fallback = (BATCH_STATUS_ENUM[status] || {}).text;
  const label = statusText || fallback || TEXT.EMPTY;
  const showCount =
    (status === BATCH_STATUS.SUCCESS || status === BATCH_STATUS.PARTIAL) &&
    Number(totalCount) > 0;
  return showCount
    ? `${label}（${Number(successCount) || 0}/${totalCount}）`
    : label;
};

/**
 * 「生成中」自动刷新的播报（§3.2 接线，2026-09-11）。
 *
 * 需求口径：从「生成二维码物料」创建后跳进记录页，行会停在「生成中」，
 * 原来只能人肉点「查询」/刷浏览器才更新。接线后由页面静默复查，
 * **只有批次真的从「生成中」变成终态时**才刷新表格并播报一次，
 * 所以这里三条文案是运营在整个等待过程里唯一会看到的打断。
 *
 * 文案规则（与 BATCH_STATUS_LABEL 同源，不另起一套口径）：
 *   - 状态词一律取 BATCH_STATUS_LABEL（含「生成成功（100/100）」这种括号计数）；
 *   - 一定带批次号：运营可能同时等多个批次，不带号等于让人猜是哪批好了；
 *   - 部分成功必须把缺口和出路（补全剩余）写进同一句，别让运营再去算 100−96。
 *
 * @param {{batchNo?:string,status?:number,statusText?:string,successCount?:number,totalCount?:number}} [batch] 列表行
 * @returns {string} 如「批次 QRC2026… 生成成功（100/100），可点「下载」取整版」
 */
export const POLL_RESOLVED_TOAST = (batch) => {
  // 自己兑 null（同 BATCH_STATUS_LABEL）：调用方拿不到行时不能整页抛错。
  const { batchNo = '', status, totalCount, successCount } = batch || {};
  const label = BATCH_STATUS_LABEL(batch);
  const prefix = batchNo ? `批次 ${batchNo} ` : '';
  if (status === BATCH_STATUS.PARTIAL) {
    const missing = Math.max(
      (Number(totalCount) || 0) - (Number(successCount) || 0),
      0,
    );
    return `${prefix}${label}，还缺 ${missing} 张，可点「${BTN.TOPUP}」`;
  }
  if (status === BATCH_STATUS.FAIL) {
    // 需求原文的失败兜底文案，接在状态后面读起来是一整句
    return `${prefix}${label}，${TEXT.GENERATE_FAIL_TOAST}`;
  }
  if (status === BATCH_STATUS.SUCCESS) {
    return `${prefix}${label}，可点「${BTN.DOWNLOAD}」取整版`;
  }
  return `${prefix}${label}`;
};

/**
 * 播报语气：成功绿勾 / 部分成功橙警告 / 失败红叉。
 * 页面按 `message[tone]()` 调用，别在页面里再写一遍 status→tone 的映射。
 */
export const POLL_RESOLVED_TONE = {
  [BATCH_STATUS.SUCCESS]: 'success',
  [BATCH_STATUS.PARTIAL]: 'warning',
  [BATCH_STATUS.FAIL]: 'error',
};

/**
 * 轮询期间挂在表格上方的常驻提示条文案（§3.2）。
 *
 * 🔴 为什么是常驻提示条而不是 toast：需求场景是「创建后进来等结果」，
 * 而 antd message 3 秒自动消失 —— 提示消失了状态却没变，等于告诉用户「刚才那下是空的」，
 * 比不给提示更糟。等待状态必须能一直在，且要写明「本页会自动刷新」，
 * 否则运营仍会习惯性去点「查询」（这正是本次要解决的缺陷）。
 */
export const POLL_TIP = {
  /** 等待中：intervalSec / waitedSec 由提示条自己的 1 秒心跳取，不牵动表格重渲染 */
  RUNNING: (intervalSec, waitedSec) =>
    `二维码正在生成中，本页每 ${intervalSec} 秒自动刷新状态，无需手动点「查询」（已等待 ${waitedSec} 秒）`,
  /** 用户主动停止：必须留一条能反悔的入口，否则表就永久停在旧状态而用户不知道 */
  STOPPED: '已停止自动刷新，本页不会再更新生成状态，需要时点右侧按钮恢复。',
  /** 轮询请求失败：静默一次就停（不 5 秒弹一条错误刷屏），交给用户决定 */
  FAILED: '自动刷新失败，已暂停，不影响你手动查询。',
  /** 一次结束多个批次的兼底播报（多标签/他人同批操作时才会出现） */
  MULTI: (count) => `${count} 个批次的生成状态已更新`,
};

/**
 * 是否绑定（契约 §3.3：明细行 bindStatus 0/1，展示用 bindStatusText）
 * 供「二维码绑定详情页」的筛选项与列共用；单张能否下载只看详情行 downloadable 一个布尔（v1.2）
 */
export const BIND_STATUS = {
  UNBOUND: 0,
  BOUND: 1,
};

export const BIND_STATUS_ENUM = {
  [BIND_STATUS.UNBOUND]: { text: '未绑定' },
  [BIND_STATUS.BOUND]: { text: '已绑定' },
};

/**
 * 单张二维码文件名（契约 §3.4 / §8-A3 定稿：统一 `二维码{code}.png`，无「物料」前缀）。
 * ⚠ ZIP 内图片名、单张下载文件名都以服务端下发的 fileName 为准，本常量仅作接口未给时的兼底，
 *   两个页面必须 import 本函数，不要各写一份（旧记录页写法「二维码物料{code}」作废）。
 */
export const QRCODE_FILE_NAME = (code) => `二维码${code || ''}.png`;

/** 部分成功批次缺角提示（契约 §4：status=3 列表角标） */
export const PARTIAL_MISSING_TIP = (missing) =>
  `本批次缺 ${missing} 张，可补足后再下载整版`;

/** ZIP 打包不全提示（契约 §3.4：partial=true 时必须弹，别静默让运营拿去印刷） */
export const PARTIAL_ZIP_TOAST = (expected, actual) =>
  `本次应有 ${expected} 张，实际打包 ${actual} 张，请联系运营核对`;

/** 批量 ZIP 内的文件夹名（需求：记录页 ZIP 命名为「二维码物料」，同事侧使用） */
export const QRCODE_ZIP_DIR_NAME = '二维码物料';

/**
 * 打包耗时估算（秒）：SIT 实测约 0.2 秒/张（1 张 0.68s / 100 张 20.92s），
 * 且**服务端每次都现场重打包、不缓存**（同一批连点两次实测 20.92s / 19.93s）。
 * 记录页 / 详情页共用（zipNotice.jsx），不要各写一份。
 */
export const ZIP_ETA_SECONDS = (count) => Math.max(1, Math.ceil(count * 0.2));

/** 需求固定文案，逐字引用，禁止在组件里改字 */
export const TEXT = {
  /**
   * ⚠ 契约 §3.1：文案由服务端 data.toast 下发，本常量只作后端未给时的兼底，
   *   页面统一写 message.success(res.data.toast || TEXT.GENERATING_TOAST)。
   */
  GENERATING_TOAST: '二维码物料正在生成，请耐心等候！',
  /** 生成记录页操作按钮置灰 hover 提示（需求原文；契约 §4 生成中态） */
  GENERATING_DISABLED_TIP: '物料生成中，暂不支持此操作',
  /** 「补全剩余」按钮 hover 文案（契约 §3.6） */
  TOPUP_TIP: '此操作仅补充本批次生成失败的二维码',
  /**
   * 记录页「绑定情况」表头感叹号 hover 文案（需求原文 2026-09-08）：
   * 说明这一列是「完成绑定的物料数量 / 生成的物料数量」两个数拼出来的比值，
   * 分母口径与实际生成数（successCount）一致，不是计划总数（totalCount）。
   */
  BIND_RATIO_TIP: '完成绑定的物料数量/生成的物料数量',
  /**
   * 「补全剩余」确认弹窗文案（契约 §3.6）：missing 用列表行的 totalCount−successCount 估算；
   * ⚠ 实际补足数以提交响应 data.need 为准（服务端提交瞬间重算），弹窗文案只做事前告知。
   */
  TOPUP_CONFIRM_CONTENT: (missing) =>
    `本批次还有 ${missing} 个二维码未生成，确认提交补全？仅补足本批缺失/失败的码，提交后批次将回到「生成中」。`,
  /**
   * 补足成功兼底文案：契约同创建/下载，优先展示服务端 data.toast，本常量只在未给时用；
   * need 用响应值不用弹窗估算值（§3.6「提交后以响应 need 为准」）。
   */
  TOPUP_SUBMIT_TOAST: (need) => `已提交补足 ${need} 个二维码，请稍后刷新查看。`,
  /**
   * 补足失败兼底（契约 §3.6）：非 00000 时页面一律 toast 后端 msg 原文
   * （如「该批次正在生成中，请勿重复提交」），本常量只在无 msg / 网络层 reject 时用。
   */
  TOPUP_FAIL_TOAST: '补全提交失败，请重试',
  /** 轮询到达上限后的常驻提示（契约 §3.2） */
  POLL_TIMEOUT_TIP: '仍在生成中，可稍后刷新查看',
  /** 未勾选点击批量下载（需求原文） */
  NO_SELECTION_TOAST: '请先选择二维码',
  /** 生成失败兜底提示（需求原文） */
  GENERATE_FAIL_TOAST: '生成失败，请联系产研进行处理',
  LIST_FAIL_TOAST: '获取列表失败，请重试',
  SUBMIT_FAIL_TOAST: '提交失败，请检查网络后重试',
  /**
   * 单张下载现取签名链接失败（契约 §3.5：现取现用，取不到就不能下）。
   * ★2026-09-07：单张已改走端侧 blob，**当前无调用方**（契约 §11.9）；
   *   常量与一级路径一起保留，恢复取链时一并接回。
   */
  IMAGE_LINK_FAIL_TOAST: '获取下载链接失败，请重试',
  /**
   * 记录页 / 详情页 ZIP 下载失败兜底（契约 §3.4）。
   * ⚠ 后端非 00000 时会带可读 msg（如「该批次二维码尚未生成完成或已失败，暂不可下载」），
   *   页面一律 `message.error(res.msg || TEXT.DOWNLOAD_FAIL_TOAST)`，本常量只在无 msg 时用。
   */
  DOWNLOAD_FAIL_TOAST: '下载失败，请重试',
  COPY_FAIL_TOAST: '图片跨域受限，已在新窗口打开，请右键另存',
  NO_IMAGE_TOAST: '二维码图片暂不可用，无法下载',
  /** 打包等待提示（右下角 notification 卡，记录页 / 详情页共用 zipNotice.jsx） */
  ZIP_NOTICE_TITLE: '正在打包二维码物料',
  ZIP_NOTICE_HINT: '请勿关闭或刷新本页',
  /** 签名链到手、已交给浏览器时的收尾文案（假进度补到 100% 那一瞬） */
  ZIP_NOTICE_DELIVERED: (count) => `${count} 张已交给浏览器下载`,
  EMPTY: '--',
};

/** 时间展示格式（绑定时间、生成时间统一） */
export const TIME_FORMAT = 'YYYY-MM-DD HH:mm:ss';

/** 二维码缩略图尺寸（表格内） */
export const QRCODE_THUMB_SIZE = 48;

/**
 * 幂等键（契约 §3.1 / §3.6：前缀按场景不同；生命周期是「一次弹窗会话」而不是「一次点击」）
 * @param {string} [batchNo] 传了即补全场景，返回 `MATTOPUP:{batchNo}:{uuid}`；
 *                  不传为创建场景，返回 `MATBATCH:{uuid}`
 * 内网 http（非安全上下文）下 crypto.randomUUID 不可用，必须降级。
 * 注意：真正的防重复签发由后端幂等兜底，前端 key 只是提交标识。
 */
const genUuid = () => {
  if (
    typeof crypto !== 'undefined' &&
    typeof crypto.randomUUID === 'function'
  ) {
    return crypto.randomUUID();
  }
  return `${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
};

export const genIdempotencyKey = (batchNo) =>
  batchNo ? `MATTOPUP:${batchNo}:${genUuid()}` : `MATBATCH:${genUuid()}`;
